import React, { useState, useEffect, useContext } from 'react';
import api from '../utils/api';
import { UserContext } from '../App';
import Logo from './Logo';
import { useNavigate, useLocation } from 'react-router-dom';

export const FINANCIAL_CATEGORIES = [
  'Bank account',
  'Credit Card',
  'Investment account',
  'Pension',
  'Insurance',
  'Property',
  'Utilities'
];

const TYPE_OPTIONS = {
  'Bank account': ['Savings', 'Checking', 'Mortgage'],
  'Credit Card': ['Credit Card'],
  'Investment account': ['Cash ISA', 'Stocks & Shares ISA', 'Trading'],
  'Pension': ['managed', 'SIPP'],
  'Insurance': ['Home', 'Contents', 'Car', 'Phone', 'Travel'],
  'Property': ['Residential', 'Commercial', 'Rental'],
  'Utilities': ['Water', 'Gas', 'Electricity', 'TV', 'Broadband', 'Mobile phone', 'Home phone']
};

const INSTITUTION_OPTIONS = {
  'Bank account': ['NatWest', 'Lloyds Bank', 'Nationwide', 'Halifax', 'HSBC', 'Monzo', 'Barclays'],
  'Credit Card': ['American Express', 'NatWest', 'Lloyds Bank', 'Nationwide', 'Halifax', 'HSBC', 'Barclays'],
  'Investment account': ['ii', 'Hargreaves Landsdown', 'Barclays', 'AJ Bell', 'IG Group', 'Aviva'],
  'Pension': ['ii', 'Scottish Widows', 'Smart Pension'],
  'Insurance': ['Compare the Market', 'Aviva', 'Admiral', 'LV'],
  'Property': [], // free form
  'Utilities': {
    'Water': ['Thames Water', 'Severn Trent', 'United Utilities', 'Yorkshire Water', 'Anglian Water', 'Scottish Water', 'Welsh Water (Dwr Cymru)', 'Southern Water', 'Northumbrian Water', 'South West Water'],
    'Gas': ['Octopus Energy', 'British Gas', 'E.ON Next', 'EDF Energy', 'Scottish Power', 'OVO Energy', 'SSE'],
    'Electricity': ['Octopus Energy', 'British Gas', 'E.ON Next', 'EDF Energy', 'Scottish Power', 'OVO Energy', 'SSE'],
    'TV': ['Sky', 'Virgin Media', 'BT TV', 'NOW'],
    'Broadband': ['BT', 'Sky', 'Virgin Media', 'TalkTalk', 'Plusnet', 'Vodafone', 'EE', 'Zen Internet', 'Hyperoptic', 'Community Fibre'],
    'Mobile phone': ['EE', 'O2', 'Vodafone', 'Three', 'giffgaff', 'Tesco Mobile', 'Sky Mobile', 'VOXI', 'SMARTY', 'Lebara'],
    'Home phone': ['BT', 'Sky', 'Virgin Media', 'TalkTalk', 'Plusnet', 'Vodafone']
  }
};

function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(30,40,60,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', padding: '32px 28px 24px 28px', borderRadius: 16, minWidth: 340, maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(30,40,60,0.18)', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 18, background: 'none', border: 'none', fontSize: 26, color: '#888', cursor: 'pointer', lineHeight: 1 }} aria-label="Close">&times;</button>
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontWeight: 600, fontSize: 22, color: '#222' }}>Add Financial Asset</h2>
          <p style={{ color: '#666', fontSize: 15, marginTop: 8 }}>Enter the details of your financial asset below.</p>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function FinancialAssets() {
  const { currentUser, users, setCurrentUser } = useContext(UserContext);
  const [assets, setAssets] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
  const [activeCategory, setActiveCategory] = useState('Bank account');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [beneficiaryOwners, setBeneficiaryOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [beneficiaryAssets, setBeneficiaryAssets] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [addAssetDropdownOpen, setAddAssetDropdownOpen] = useState(false);
  const [importingOBP, setImportingOBP] = useState(false);
  const [obpModalOpen, setObpModalOpen] = useState(false);
  const [obpOptions, setObpOptions] = useState({ types: [], institutions: [] });
  const [obpForm, setObpForm] = useState({ selectedType: '', selectedInstitution: '' });

  // Display rules
  const HIDE_ACCOUNT_VALUE_CATEGORIES = ['Bank account', 'Credit Card', 'Investment account', 'Pension', 'Utilities'];
  const hideAccountAndValue = HIDE_ACCOUNT_VALUE_CATEGORIES.includes(activeCategory);
  const isInsurance = activeCategory === 'Insurance';

  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  // Fetch financial assets for asset holder (owner view)
  useEffect(() => {
    if (currentUser.type === 'owner') {
      api.get('/assets').then(res => {
        const holderAssets = res.data.filter(a => a.ownerId === assetHolder.id && FINANCIAL_CATEGORIES.includes(a.typeCategory));
        setAssets(holderAssets);
        if (holderAssets.length > 0) {
          const firstCat = FINANCIAL_CATEGORIES.find(cat => holderAssets.some(a => a.typeCategory === cat));
          setActiveCategory(firstCat || FINANCIAL_CATEGORIES[0]);
        } else {
          setActiveCategory(FINANCIAL_CATEGORIES[0]);
        }
      });
    }
  }, [currentUser, assetHolder.id]);

  // Fetch nominees and assets for nominee view
  useEffect(() => {
    if (currentUser.type === 'nominee') {
      Promise.all([
        api.get('/nominees'),
        api.get('/auth/users')
      ]).then(([benRes, usersRes]) => {
        const myBenRecords = benRes.data.filter(b => b.email === currentUser.email);
        const allUsers = usersRes.data;
        const uniqueOwnerIds = [...new Set(myBenRecords.map(b => b.ownerId))];
        // Ensure we don't have duplicate owner IDs
        const finalUniqueOwnerIds = [...new Set(uniqueOwnerIds)];
        const owners = finalUniqueOwnerIds.map(ownerId => {
          const ownerUser = allUsers.find(u => u.id === ownerId);
          return ownerUser ? { ownerId, ownerName: ownerUser.name, ownerEmail: ownerUser.email } : { ownerId, ownerName: ownerId, ownerEmail: '' };
        });
        setBeneficiaryOwners(owners);
        
        // Always check for a pendingOwnerId from localStorage
        const storedOwnerId = localStorage.getItem('selectedOwnerId');
        if (storedOwnerId && finalUniqueOwnerIds.includes(storedOwnerId)) {
          setSelectedOwnerId(storedOwnerId);
          localStorage.removeItem('selectedOwnerId');
        } else if (owners.length > 0) {
          setSelectedOwnerId(owners[0].ownerId);
        }
      });
    }
  }, [currentUser]);

  // Fetch assets for selected owner (nominee view)
  useEffect(() => {
    if (currentUser.type === 'nominee' && selectedOwnerId) {
      api.get('/assets').then(assetRes => {
        api.get('/nominees').then(benRes => {
          const myBenRecords = benRes.data.filter(b => b.email === currentUser.email && b.ownerId === selectedOwnerId);
          const ownerAssets = assetRes.data.filter(a => a.ownerId === selectedOwnerId && FINANCIAL_CATEGORIES.includes(a.typeCategory));
          setBeneficiaryAssets(ownerAssets);
          if (ownerAssets.length > 0) {
            const firstCat = FINANCIAL_CATEGORIES.find(cat => ownerAssets.some(a => a.typeCategory === cat));
            setActiveCategory(firstCat || FINANCIAL_CATEGORIES[0]);
          } else {
            setActiveCategory(FINANCIAL_CATEGORIES[0]);
          }
        });
      });
    }
  }, [currentUser, selectedOwnerId]);

  // Fetch OBP options
  useEffect(() => {
    if (currentUser.type === 'owner') {
      // Try both paths for debugging
      api.get('/obp/options').then(res => {
        console.log('Fetched OBP options:', res.data);
        setObpOptions(res.data);
      }).catch(err => {
        console.error('Failed to fetch OBP options from /obp/options:', err);
        // Try /api/obp/options as fallback
        api.get('/api/obp/options').then(res2 => {
          console.log('Fetched OBP options (fallback):', res2.data);
          setObpOptions(res2.data);
        }).catch(err2 => {
          console.error('Failed to fetch OBP options from /api/obp/options:', err2);
        });
      });
    }
  }, [currentUser]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // Step logic
  const resetForm = () => {
    setForm({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
    setStep(0);
  };

  const openModal = () => { console.debug('opening financial asset modal'); setModalOpen(true); resetForm(); };
  const closeModal = () => { setModalOpen(false); resetForm(); };

  const handleCategorySelect = e => {
    setForm(f => ({ ...f, typeCategory: e.target.value, type: '', institution: '' }));
    setStep(1);
  };
  const handleTypeSelect = e => {
    setForm(f => ({ ...f, type: e.target.value }));
    setStep(2);
  };
  const handleInstitutionSelect = e => {
    setForm(f => ({ ...f, institution: e.target.value }));
    setStep(3);
  };
  const handleFreeInstitution = e => {
    setForm(f => ({ ...f, institution: e.target.value }));
  };
  const handleAccountNo = e => setForm(f => ({ ...f, accountNo: e.target.value }));
  const handleValue = e => setForm(f => ({ ...f, value: e.target.value }));

  const addAsset = async e => {
    e.preventDefault();
    try {
      const newAsset = { ...form, ownerId: assetHolder.id, notes: form.notes || '' };
      await api.post('/assets', newAsset);
      const res = await api.get('/assets');
      setAssets(res.data.filter(a => a.ownerId === assetHolder.id && FINANCIAL_CATEGORIES.includes(a.typeCategory)));
      closeModal();
    } catch (err) {
      if (err.response) {
        console.error('Backend error:', err.response.data);
        alert('Failed adding asset: ' + (err.response.data.message || JSON.stringify(err.response.data)));
      } else {
        console.error(err);
        alert('Failed adding asset (network or unknown error)');
      }
    }
  };

  // Add assets from OBP
  const handleAddAssetsAutomatically = async () => {
    setAddAssetDropdownOpen(false);
    setObpModalOpen(true);
  };

  const handleObpImport = async () => {
    if (!obpForm.selectedType || !obpForm.selectedInstitution) {
      alert('Please select both Asset Type and Institution');
      return;
    }

    setImportingOBP(true);
    setObpModalOpen(false);
    
    try {
      // Use owner's first/last name and email for OBP API
      const [firstName, ...lastArr] = (assetHolder.name || '').split(' ');
      const lastName = lastArr.join(' ');
      const email = assetHolder.email;
      const res = await api.post('/obp/assets', { firstName, lastName, email });
      
      // Filter for financial assets only and match the selected type and institution
      const financialAssets = res.data.filter(asset => 
        ['Bank account', 'Credit Card', 'Investment account', 'Pension', 'Insurance'].includes(asset.typeCategory) &&
        asset.type === obpForm.selectedType &&
        asset.institution === obpForm.selectedInstitution
      );
      
      for (const obpAsset of financialAssets) {
        const { type, typeCategory, institution, accountNo, value, notes } = obpAsset;
        await api.post('/assets', { ownerId: assetHolder.id, type, typeCategory, institution, accountNo, value, notes });
      }
      
      const assetsRes = await api.get('/assets');
      setAssets(assetsRes.data.filter(a => a.ownerId === assetHolder.id && FINANCIAL_CATEGORIES.includes(a.typeCategory)));
      alert('Financial assets imported from OBP!');
      
      // Reset form
      setObpForm({ selectedType: '', selectedInstitution: '' });
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        alert('OBP import failed: ' + err.response.data.error);
      } else {
        alert('OBP import failed.');
      }
    } finally {
      setImportingOBP(false);
    }
  };

  // Sidebar styles
  const sidebarStyle = {
    width: 220,
    background: 'var(--dusk-700)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    padding: '32px 0 18px 0',
    boxShadow: '2px 0 12px rgba(30,40,60,0.04)',
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    zIndex: 100,
  };
  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 180,
    minWidth: 0,
    maxWidth: '80vw',
    transition: 'transform 0.3s',
    transform: 'translateX(-100%)',
  };
  const mobileSidebarOpenStyle = {
    ...mobileSidebarStyle,
    transform: 'translateX(0)',
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mist-100)' }}>
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 12px', fontSize: 22, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}
          aria-label="Open menu"
        >
          ☰
        </button>
      )}
      {/* Sidebar */}
      <nav
        style={
          isMobile
            ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
            : sidebarStyle
        }
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <Logo size={36} />
          </div>
          {/* User indicator */}
          <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 12, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, boxShadow: 'var(--shadow-soft)' }}>
            <span>Logged in as:</span>
            <span style={{ fontWeight: 700 }}>{currentUser.name}</span>
            <span style={{ fontSize: 13, opacity: 0.85 }}>{currentUser.email}</span>
            {currentUser.role && <span style={{ fontSize: 13, opacity: 0.7 }}>({currentUser.role})</span>}
          </div>
          
          {/* Dashboard */}
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
              style={{
                background: location.pathname === '/dashboard' ? 'var(--sage-600)' : '#29304a',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '12px 18px',
                fontWeight: location.pathname === '/dashboard' ? 600 : 500,
                fontSize: 16,
                margin: '0 18px',
                width: 'calc(100% - 36px)',
                cursor: 'pointer',
                textAlign: 'left',
                outline: 'none',
                boxShadow: location.pathname === '/dashboard' ? 'var(--shadow-soft)' : 'none'
              }}
            >
              Dashboard
            </button>
          </div>

          {/* Assets Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assets</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/financial-assets'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/financial-assets' ? 'var(--sage-600)' : '#29304a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/financial-assets' ? 600 : 500, fontSize: 16, margin: '0 18px', width: 'calc(100% - 36px)', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/financial-assets' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Financial Assets
              </button>
              <button
                onClick={() => { navigate('/legal-assets'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/legal-assets' ? 'var(--sage-600)' : '#29304a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/legal-assets' ? 600 : 500, fontSize: 16, margin: '0 18px', width: 'calc(100% - 36px)', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/legal-assets' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Legal Assets
              </button>
              <button
                onClick={() => { navigate('/other-assets'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/other-assets' ? 'var(--sage-600)' : '#29304a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/other-assets' ? 600 : 500, fontSize: 16, margin: '0 18px', width: 'calc(100% - 36px)', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/other-assets' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Other Assets
              </button>
            </div>
          </div>

          {/* Account Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Account</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/account'); setSidebarOpen(false); }}
                style={{ background: location.pathname === '/account' ? 'var(--sage-600)' : '#29304a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/account' ? 'var(--shadow-soft)' : 'none' }}
              >
                Account
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '60px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto' }}>
        <div style={{ padding: 20, position: 'relative' }}>
          {currentUser.type === 'nominee' ? (
            <>
              {/* Owner selector dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>
                  {(() => {
                    const donor = beneficiaryOwners.find(o => o.ownerId === selectedOwnerId);
                    if (!donor) return 'Owner\'s Financial Assets';
                    let profile = {};
                    try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                    const displayName = (profile.firstName && profile.lastName)
                      ? `${profile.firstName} ${profile.lastName}`
                      : (donor.ownerName || 'Owner');
                    return `${displayName}'s Financial Assets`;
                  })()}
                </h1>
                <select
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, minWidth: 180 }}
                >
                  {beneficiaryOwners.map(o => (
                    <option key={o.ownerId} value={o.ownerId}>{(() => {
                      let profile = {};
                      try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                      return (profile.firstName && profile.lastName)
                        ? `${profile.firstName} ${profile.lastName}`
                        : (o.ownerName || o.ownerId);
                    })()}</option>
                  ))}
                </select>
              </div>
              {/* Category Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, marginTop: 8, flexWrap: 'wrap' }}>
                {FINANCIAL_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 12,
                      border: 'none',
                      background: activeCategory === cat ? 'var(--sage-600)' : 'var(--mist-200)',
                      color: activeCategory === cat ? '#fff' : 'var(--dusk-700)',
                      fontWeight: activeCategory === cat ? 600 : 400,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: activeCategory === cat ? 'var(--shadow-soft)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No financial assets in this category yet.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Institution</th>
                      {!hideAccountAndValue && (
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>{isInsurance ? 'Policy No' : 'Account No'}</th>
                      )}
                      {!hideAccountAndValue && (
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Value</th>
                      )}
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 8px' }}>{a.type}</td>
                        <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                        {!hideAccountAndValue && (
                          <td style={{ padding: '10px 8px' }}>{a.accountNo || (isInsurance ? '-' : '')}</td>
                        )}
                        {!hideAccountAndValue && (
                          <td style={{ padding: '10px 8px' }}>${Number(a.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        )}
                        <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <h1 style={{ marginBottom: 32 }}>Financial Assets</h1>
              <div style={{ position: 'absolute', top: 20, right: 20 }}>
                <button
                  onClick={() => setAddAssetDropdownOpen(open => !open)}
                  style={{ padding: '10px 18px', fontSize: 16, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', boxShadow: 'var(--shadow-soft)', fontWeight: 600 }}
                  disabled={importingOBP}
                >
                  Add financial asset
                </button>
                {addAssetDropdownOpen && (
                  <div style={{ position: 'absolute', right: 0, top: 44, background: '#fff', border: '1px solid #ccc', borderRadius: 8, boxShadow: '0 4px 16px rgba(30,40,60,0.13)', zIndex: 100, minWidth: 220 }}>
                    <button
                      onClick={handleAddAssetsAutomatically}
                      style={{ width: '100%', padding: '12px 18px', background: 'none', border: 'none', color: 'var(--sage-600)', fontWeight: 600, fontSize: 15, textAlign: 'left', cursor: 'pointer', borderRadius: '8px 8px 0 0' }}
                      disabled={importingOBP}
                    >
                      {importingOBP ? 'Importing...' : 'Import from OBP'}
                    </button>
                    <button
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); openModal(); setAddAssetDropdownOpen(false); console.log('Add manually mousedown → modal open'); }}
                      style={{ width: '100%', padding: '12px 18px', background: 'none', border: 'none', color: 'var(--sage-600)', fontWeight: 600, fontSize: 15, textAlign: 'left', cursor: 'pointer', borderRadius: '0 0 8px 8px' }}
                    >
                      Add manually
                    </button>
                  </div>
                )}
              </div>
              {/* Category Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, marginTop: 8, flexWrap: 'wrap' }}>
                {FINANCIAL_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 12,
                      border: 'none',
                      background: activeCategory === cat ? 'var(--sage-600)' : 'var(--mist-200)',
                      color: activeCategory === cat ? '#fff' : 'var(--dusk-700)',
                      fontWeight: activeCategory === cat ? 600 : 400,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: activeCategory === cat ? 'var(--shadow-soft)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {assets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No financial assets in this category yet.</p>
                  <p>Click <b>Add financial asset</b> to get started.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Institution</th>
                      {!hideAccountAndValue && (
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>{isInsurance ? 'Policy No' : 'Account No'}</th>
                      )}
                      {!hideAccountAndValue && (
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Value</th>
                      )}
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.filter(a => a.typeCategory === activeCategory).map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 8px' }}>{a.type}</td>
                        <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                        {!hideAccountAndValue && (
                          <td style={{ padding: '10px 8px' }}>{a.accountNo || (isInsurance ? '-' : '')}</td>
                        )}
                        {!hideAccountAndValue && (
                          <td style={{ padding: '10px 8px' }}>${Number(a.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        )}
                        <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
        {/* Modal for asset holder only */}
        {currentUser.type === 'owner' && (
          <Modal open={modalOpen} onClose={closeModal}>
            <form onSubmit={addAsset} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {step === 0 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Asset Category<br />
                    <select name="typeCategory" value={form.typeCategory} onChange={handleCategorySelect} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}>
                      <option value="" disabled>Select category</option>
                      {FINANCIAL_CATEGORIES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {step === 1 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Asset Type<br />
                    <select name="type" value={form.type} onChange={handleTypeSelect} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}>
                      <option value="" disabled>Select type</option>
                      {TYPE_OPTIONS[form.typeCategory]?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {step === 2 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Institution<br />
                    {form.typeCategory === 'Property' ? (
                      <input name="institution" placeholder="Institution" value={form.institution} onChange={handleFreeInstitution} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }} />
                    ) : (
                      <select name="institution" value={form.institution} onChange={handleInstitutionSelect} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}>
                        <option value="" disabled>Select institution</option>
                        {Array.isArray(INSTITUTION_OPTIONS[form.typeCategory])
                          ? INSTITUTION_OPTIONS[form.typeCategory].map(opt => <option key={opt} value={opt}>{opt}</option>)
                          : (INSTITUTION_OPTIONS[form.typeCategory]?.[form.type] || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    )}
                  </label>
                </div>
              )}
              {step === 3 && (
                <>
                  {!hideAccountAndValue && (
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500, color: '#333' }}>{form.typeCategory === 'Insurance' ? 'Policy Number (optional)' : 'Account No'}<br />
                        <input name="accountNo" placeholder={form.typeCategory === 'Insurance' ? 'Policy Number (optional)' : 'Account No'} value={form.accountNo} onChange={handleAccountNo} style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }} />
                      </label>
                    </div>
                  )}
                  {!hideAccountAndValue && (
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500, color: '#333' }}>Value<br />
                        <input name="value" placeholder="Value" type="number" value={form.value} onChange={handleValue} required style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }} />
                      </label>
                    </div>
                  )}
                  <div style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Notes<br />
                      <input name="notes" placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }} />
                    </label>
                  </div>
                  <button type="submit" style={{ padding: '10px 0', fontSize: 17, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, marginTop: 10, fontWeight: 600, letterSpacing: 0.5, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}>Save</button>
                </>
              )}
            </form>
          </Modal>
        )}

        {/* OBP Import Modal */}
        {currentUser.type === 'owner' && (
          <Modal open={obpModalOpen} onClose={() => setObpModalOpen(false)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ marginBottom: 0 }}>
                <label style={{ fontWeight: 500, color: '#333' }}>Asset Type<br />
                  <select 
                    value={obpForm.selectedType} 
                    onChange={e => setObpForm(f => ({ ...f, selectedType: e.target.value }))} 
                    required 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}
                  >
                    <option value="" disabled>Select asset type</option>
                    {(Array.isArray(obpOptions.types) ? obpOptions.types : []).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ marginBottom: 0 }}>
                <label style={{ fontWeight: 500, color: '#333' }}>Institution<br />
                  <select 
                    value={obpForm.selectedInstitution} 
                    onChange={e => setObpForm(f => ({ ...f, selectedInstitution: e.target.value }))} 
                    required 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', marginTop: 6 }}
                  >
                    <option value="" disabled>Select institution</option>
                    {(Array.isArray(obpOptions.institutions) ? obpOptions.institutions : []).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button 
                  type="button"
                  onClick={handleObpImport}
                  disabled={importingOBP}
                  style={{ 
                    flex: 1, 
                    padding: '10px 0', 
                    fontSize: 17, 
                    background: 'var(--sage-600)', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 12, 
                    fontWeight: 600, 
                    letterSpacing: 0.5, 
                    boxShadow: 'var(--shadow-soft)', 
                    cursor: importingOBP ? 'not-allowed' : 'pointer',
                    opacity: importingOBP ? 0.6 : 1
                  }}
                >
                  {importingOBP ? 'Importing...' : 'Import from OBP'}
                </button>
                <button 
                  type="button"
                  onClick={() => setObpModalOpen(false)}
                  style={{ 
                    padding: '10px 16px', 
                    fontSize: 17, 
                    background: 'var(--mist-200)', 
                    color: 'var(--dusk-700)', 
                    border: 'none', 
                    borderRadius: 12, 
                    fontWeight: 500, 
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
} 